import DOMPurify from 'dompurify';

/**
 * Sanitize HTML content to prevent XSS attacks.
 * Uses DOMPurify to remove dangerous elements and attributes.
 *
 * @param html - The HTML string to sanitize
 * @returns Sanitized HTML string safe for dangerouslySetInnerHTML
 */
export function sanitizeHtml(html: string | null | undefined): string {
  if (!html) return '';

  // Configure DOMPurify to allow common formatting tags
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      'p',
      'br',
      'strong',
      'b',
      'em',
      'i',
      'u',
      'ul',
      'ol',
      'li',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'blockquote',
      'pre',
      'code',
      'a',
      'img',
      'table',
      'thead',
      'tbody',
      'tr',
      'th',
      'td',
      'div',
      'span',
    ],
    ALLOWED_ATTR: ['href', 'title', 'src', 'alt', 'width', 'height', 'class'],
    ALLOW_DATA_ATTR: false,
    // Block javascript: and data: URLs
    ALLOWED_URI_REGEXP:
      /^(?:(?:https?|mailto):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
  });
}

/**
 * Create a sanitized HTML object for use with dangerouslySetInnerHTML.
 *
 * @param html - The HTML string to sanitize
 * @returns Object with __html property for React's dangerouslySetInnerHTML
 */
export function createSanitizedHtml(
  html: string | null | undefined,
): { __html: string } {
  return { __html: sanitizeHtml(html) };
}
